import { useState, useEffect } from 'react';

// Extracted from StudentProfile.js (Phase 3 of the platform-health plan:
// getting the file under ~600 lines needs pulling logic into hooks, not
// just JSX into components). Generates and caches per-student AI teaching
// recommendations (via the OpenAI proxy) from the profile's free-text
// fields (experience/goals/preferences/hobbies/strengths-weaknesses).
export function useAIRecommendations({ studentId, studentData }) {
  const aiRecsStorageKey = (studentId && studentId !== 'new') ? `novas_ai_recs_${studentId}` : null;
  const loadAiRecsFromStorage = () => {
    if (!aiRecsStorageKey) return {};
    try {
      const raw = localStorage.getItem(aiRecsStorageKey);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return (parsed && typeof parsed === 'object') ? parsed : {};
    } catch { return {}; }
  };
  const [aiRecommendations, setAiRecommendations] = useState(loadAiRecsFromStorage);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');

  // Re-load when navigating between students (the key changes).
  useEffect(() => {
    setAiRecommendations(loadAiRecsFromStorage());
    setAiError('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiRecsStorageKey]);

  const generateAIRecommendations = async () => {
    const fields = {
      proficiency: studentData.proficiency || '',
      experience: studentData.experience || '',
      goals: studentData.goals || '',
      preferences: studentData.preferences || '',
      hobbies: studentData.hobbies || '',
      strengthsWeaknesses: studentData.strengthsWeaknesses || '',
    };
    const nonEmptyCount = ['experience', 'goals', 'preferences', 'hobbies', 'strengthsWeaknesses']
      .filter(k => String(fields[k] || '').trim().length >= 4).length;
    if (nonEmptyCount === 0) {
      setAiRecommendations({});
      setAiError('');
      return;
    }

    setAiLoading(true);
    setAiError('');

    const prompt = [
      'You are an experienced ESL/EFL teaching strategist.',
      'Read this student profile and produce HIGHLY PERSONALISED teaching recommendations.',
      '',
      `CEFR level: ${fields.proficiency || '(not stated)'}`,
      `Experience: ${fields.experience || '(empty)'}`,
      `Goals: ${fields.goals || '(empty)'}`,
      `Preferences: ${fields.preferences || '(empty)'}`,
      `Hobbies: ${fields.hobbies || '(empty)'}`,
      `Strengths / Weaknesses: ${fields.strengthsWeaknesses || '(empty)'}`,
      '',
      'Return ONLY a valid JSON object — no markdown, no commentary — with this exact shape:',
      '{',
      '  "experience": "HTML string or empty",',
      '  "goals": "HTML string or empty",',
      '  "preferences": "HTML string or empty",',
      '  "hobbies": "HTML string or empty",',
      '  "strengthsWeaknesses": "HTML string or empty"',
      '}',
      '',
      'Each non-empty value MUST follow this HTML format:',
      "  <strong>Short title (≤6 words, specific to THIS student)</strong><br/>",
      "  • <em>Label:</em> concrete recommendation referencing actual content from the profile<br/>",
      "  • <em>Label:</em> ...<br/>",
      "  • <em>Label:</em> ...",
      'Three to four bullets. Each bullet 12–25 words.',
      '',
      'CRITICAL rules:',
      "  - Reference SPECIFIC things the teacher wrote (named hobby, exam, weakness, time-frame, profession).",
      '  - Never say "your hobbies" / "their goals" — name them by actual content.',
      "  - If a field is empty or under 4 characters, return empty string for that key.",
      "  - Match advice difficulty to the CEFR level when stated.",
      "  - No emojis. No placeholder words like XYZ, [TODO], etc.",
      "  - Keep total length manageable (one screen).",
    ].join('\n');

    try {
      const res = await fetch('/api/openai', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt,
          temperature: 0.55,
          maxTokens: 900,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      const { content } = await res.json();
      // Strip accidental markdown fences and parse JSON
      const clean = String(content || '').replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/i, '').trim();
      const parsed = JSON.parse(clean);
      const next = {};
      ['experience', 'goals', 'preferences', 'hobbies', 'strengthsWeaknesses'].forEach(key => {
        const v = parsed[key];
        if (typeof v === 'string' && v.trim()) next[key] = v.trim();
      });
      setAiRecommendations(next);
      // Persist so the teacher doesn't re-spend tokens on every page open.
      if (aiRecsStorageKey) {
        try { localStorage.setItem(aiRecsStorageKey, JSON.stringify(next)); } catch (_) {}
      }
    } catch (e) {
      console.warn('[AI recommendations] failed:', e?.message);
      setAiError(e?.message || 'Could not generate recommendations');
    } finally {
      setAiLoading(false);
    }
  };

  return { aiRecommendations, aiLoading, aiError, generateAIRecommendations };
}
